/**
 * The user's half of a turn, in all six of the states user-message.md describes.
 *
 * §476's progressive-disclosure model is the state machine, verbatim:
 *
 * 1. Short → plain hug bubble, nothing else.
 * 2. Long → clamped bubble (168px) + fade + footer (`Show more` · character count).
 * 3. Expanded → same bubble unclamped, fade gone, footer flips to `Show less`.
 * 4. Hover (**any** length) → two 15px actions 4px below, 12px apart, bubble untouched.
 * 5. Edit → same bubble, tighter padding + 1px border + caret.
 * 6. Attachments and mentions are content decorations, orthogonal to all of the above.
 *
 * Almost none of that is implemented here, and that is the point: `UserMessage` owns 1, 2, 3 and 5,
 * and 4 is CSS on its own row. What this file decides is only which of them apply to a message that
 * came off the wire — is it long, what rode above it, and which of the two actions has something
 * behind it.
 *
 * **Two rulings it respects.** DECISIONS §9a: `padded={false}`, because the `ConversationTurn` slot
 * this lands in already pays the 6px and two owners paint 12 where the design draws 6. DECISIONS
 * §9b: the clamp footer and the hover actions are NOT mutually exclusive — no artboard draws both,
 * but "hover (any length)" includes 438 characters, and suppressing the actions there would take
 * copy away from exactly the messages long enough to want it.
 *
 * **State 5 is not offered on a sent message, and that is a backend fact rather than a design one.**
 * There is no edit endpoint and no branching: the conversation is an append-only list of messages,
 * so an `Edit` glyph on a sent bubble could only ever lie. `MessageActions` draws a glyph if and only
 * if there is a handler behind it, so a sent message gets Copy alone and a queued one — which has
 * not been sent and CAN still be changed — gets both. That is the seam `queued` / `onEdit` exists
 * for; the queue itself is Track C's.
 */
import {
  annotationGroundsServerVersion,
  annotationGroundsVersion,
  sessionGroundFor,
  subscribeAnnotationGrounds,
} from '~/deck15/annotate/annotationGround';
import { memo, useMemo, useSyncExternalStore } from 'react';
import {
  ArtifactReference,
  AttachmentCard,
  ImageThumb,
  MessageAttachmentRow,
  MessageImageGrid,
  UserMessage,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import type { PlanningChatMessage } from '~/types/planning';
import { MentionText } from '../MentionText';
import { annotationsFromMessage, decorationsFromMessage, visiblePrompt } from '../rehydrate';
import { copyText } from './clipboard';
import { isLongMessage } from './markdown';
import styles from './UserTurn.module.css';

export interface UserTurnProps {
  message: PlanningChatMessage;
  theme: ChatTheme;
  /**
   * The message is waiting its turn behind a run in flight. Swaps the bubble's ground and adds the
   * `Queued` meta line — and is the only state in which editing is a real thing to offer.
   */
  queued?: boolean;
  /** Under the caret. Only reachable on a queued message; see the note on the component. */
  editing?: boolean;
  /** Drops a queued message from the queue. */
  onCancel?: () => void;
  /** Puts a queued message under the caret. Absent on a sent one, so no Edit glyph is drawn. */
  onEdit?: () => void;
  /**
   * The artifact's thumbnail, by slide id. Absent, or returning nothing, leaves the reference on its
   * placeholder ground — which is still a valid reference, because the pins are the point.
   *
   * An `<img src>`, and not the CSS `background-image` value `DirectoryItem.thumbnail` carries — the
   * artifact card one file over takes the same object in the other form, so the two are easy to swap.
   */
  artifactThumb?: (slideId: number | string) => string | undefined;
  /**
   * What the sentence's mentions are, and what they open. Absent, the names stay prose — which is
   * exactly right for a turn rendered somewhere that has no deck behind it.
   */
  mentionNames?: readonly string[];
  mentionTarget?: (name: string) => (() => void) | undefined;
}

export const UserTurn = memo(function UserTurn({
  message,
  theme,
  queued = false,
  editing = false,
  onCancel,
  onEdit,
  artifactThumb,
  mentionNames,
  mentionTarget,
}: UserTurnProps) {
  /**
   * The sentence, without the agent's half of it.
   *
   * The send prepends `[Editing Slide 1]`-style markers to the prompt so the agent can resolve what
   * the user pointed at, and the backend persists the prompt WITH them. They were never the user's
   * words: the chips above the bubble are what those markers look like to a reader, and the raw
   * `[Selected on canvas: Slide 4]` showing up in the bubble on reload was the bug.
   */
  const text = visiblePrompt(message.content ?? '');
  const { images, cards } = decorationsFromMessage(message);

  /**
   * THE PICTURE THIS TURN POINTED AT, which is not the artifact as it is now.
   *
   * The pins were drawn on one render, so that render is what they belong on, and the send freezes it
   * (`annotate/annotationGround.ts`). The backend keeps it apart from the attachments — it comes back as
   * `referenceImages`, never in `images` — so there is nothing to split out of the grid here.
   *
   * Three sources, in the order of how much they know: the frozen picture on a reloaded turn, the local
   * one for a turn sent in this session (its stored URL does not exist yet), and failing both the live
   * read this block used before, which is still right for every turn sent before any of this.
   */
  const groundsVersion = useSyncExternalStore(
    subscribeAnnotationGrounds,
    annotationGroundsVersion,
    annotationGroundsServerVersion,
  );
  const ground = useMemo(
    () => message.referenceImages?.[0] ?? sessionGroundFor(message.id),
    // `groundsVersion` IS a dependency — it is the store's change signal, invisible to the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [message.referenceImages, message.id, groundsVersion],
  );

  const annotated = annotationsFromMessage(message);
  const decorated = images.length > 0 || cards.length > 0 || annotated !== null;

  /**
   * Everything that rides ABOVE the sentence: attached images, attached files, and the artifact this
   * turn pointed at. Lifted out of the prop because a turn with no sentence renders it ALONE — see
   * the early return below.
   */
  const stack = decorated ? (
    <div className={styles.stack}>
      {images.length > 0 ? (
        <MessageImageGrid theme={theme}>
          {images.map((src, at) => (
            // Not decorative: the sentence beside a screenshot is usually "fix these", which
            // describes nothing. Numbered, because that is the only thing known about it —
            // "a screenshot" said six times is worse than counting them.
            <ImageThumb
              key={src || at}
              size="grid"
              src={src}
              alt={`Attached image ${at + 1}`}
              theme={theme}
            />
          ))}
        </MessageImageGrid>
      ) : null}
      {cards.length > 0 ? (
        <MessageAttachmentRow theme={theme}>
          {cards.map((card) => (
            // No `onRemove`: the message is sent, so the dot is the picture Paper drew and
            // not a control.
            <AttachmentCard
              key={card.id}
              variant="titled"
              kind={card.kind}
              name={card.name}
              theme={theme}
            />
          ))}
        </MessageAttachmentRow>
      ) : null}
      {annotated ? (
        // Last in the stack, so it is the block the bubble sits under. Its own docblock puts
        // it there — "the distance to the bubble under it is the conversation column's to
        // own" — and the sentence agrees: the words read OFF this picture ("2 is stale" is
        // the pin numbered 2), while the chips above came WITH the message rather than being
        // what it is about.
        <ArtifactReference
          theme={theme}
          pins={annotated.pins}
          src={ground ?? (annotated.slideId == null ? undefined : artifactThumb?.(annotated.slideId))}
          // `RegionReference` earns its silent default from a figcaption saying in words what
          // its crop says in pixels; this block has none, so the alt is the only place the
          // artifact is named at all. `Artifact 07` is the thread's own wording for a slide it
          // knows nothing else about — see `ThreadArtifactCard`'s numeric fallback.
          alt={annotated.slideId == null ? undefined : `Artifact ${String(annotated.slideId).padStart(2, '0')}`}
        />
      ) : null}
    </div>
  ) : undefined;

  /**
   * A TURN WITH NO WORDS IS NOT A MESSAGE WITH AN EMPTY BUBBLE.
   *
   * Pointing at three things and pressing send is a complete message — the notes are the sentence,
   * and `useDeckChat` lets it go for exactly that reason. But `UserMessage` always draws its bubble,
   * so the words being absent produced an empty rounded rectangle sitting under the picture: a
   * container for nothing, which reads as a failed send rather than as a message made of marks.
   *
   * So the picture IS the turn. Nothing else is lost with the bubble: there is no text to copy, no
   * length to clamp, and a wordless turn cannot be queued (the composer refuses to queue notes alone,
   * because a queued message takes whichever notes exist when it drains).
   *
   * `stack` already right-aligns itself and carries the column's own rhythm, so it needs no wrapper —
   * the turn slot around it supplies the padding `padded={false}` would otherwise have suppressed.
   */
  if (!text && stack) return stack;

  return (
    <UserMessage
      theme={theme}
      // DECISIONS §9a — the turn's `.user` slot already carries the 6px.
      padded={false}
      bubble={{ kind: queued ? 'queued' : 'sent', editing }}
      // The count is of the message, not of what is visible — of the stripped message, because that
      // is the message: nobody typed the markers.
      footer={isLongMessage(text) ? { characterCount: text.length } : undefined}
      // Copy gives back what the bubble shows. Copying the raw content would paste the agent's
      // markers into wherever it landed.
      onCopy={() => void copyText(text)}
      onEdit={onEdit}
      queue={queued ? { onCancel, cancelLabel: 'Remove from the queue' } : undefined}
      attachments={stack}
    >
      {/* The STRING is still the message — `footer`, `onCopy` and the clamp all read it above, and
          copying a bubble has to give back what was typed rather than what it was drawn as. This is
          only how those characters are painted. */}
      <MentionText text={text} names={mentionNames ?? []} target={mentionTarget} />
    </UserMessage>
  );
});
